import translate from '@translate';
import { ProductType } from 'src/types/product';
import { ReceivingType } from 'src/types/receiving';
import formatDate from 'src/utils/formatDateFromExcel';
import api from '@api';

const getProducts = (skus: string[]): Promise<ProductType[]> => {
    return new Promise((resolve: any): any => {
        const url = `api/client/products/eshop/skus`;
        api('post', url, skus).then((response: any) => {
            if (response.data.data.message) {
                resolve([]);
            }
            resolve(response.data.data);
        });
    });
};

export const formatData = async (fileData: any[]): Promise<ReceivingType> => {
    const dbProducts = await getProducts(fileData.map((fileData: {
        PRODUCT_SKU: string
    }) => String(fileData.PRODUCT_SKU).trim()).filter(sku => sku));
    const productMap: Record<string, { id: number; name: string; ean?: string | null; description?: string | null; productType?: string | null }> = {};
    dbProducts.forEach(product => {
        productMap[product.SKU!] = {
            id: product.ID!,
            name: product.NAME!,
            ean: product.EAN ?? null,
            description: product.DESCRIPTION ?? null,
            productType: product.PRODUCT_TYPE ?? null
        };
    });

    // Validate required fields
    const receivingErrors = [];
    const firstRow = fileData[0];

    if (!firstRow.ESTIMATED_DELIVERY) {
        receivingErrors.push({ field: 'ESTIMATED_DELIVERY', message: translate('error.empty') });
    }
    if (!firstRow.UNITS_AMOUNT) {
        receivingErrors.push({ field: 'UNITS_AMOUNT', message: translate('error.empty') });
    }
    if (!firstRow.SUPPLIER) {
        receivingErrors.push({ field: 'SUPPLIER', message: translate('error.empty') });
    }
    if (!firstRow.DELIVERY_TYPE) {
        receivingErrors.push({ field: 'DELIVERY_TYPE', message: translate('error.empty') });
    }
    if (!firstRow.DELIVERY_NOTE_NUMBER) {
        receivingErrors.push({ field: 'DELIVERY_NOTE_NUMBER', message: translate('error.empty') });
    }
    if (!firstRow.ANNOUNCED_PALLETS_COUNT && firstRow.ANNOUNCED_PALLETS_COUNT !== 0) {
        receivingErrors.push({ field: 'ANNOUNCED_PALLETS_COUNT', message: translate('error.empty') });
    }
    if (firstRow.DUTY === undefined || firstRow.DUTY === null || firstRow.DUTY === '') {
        receivingErrors.push({ field: 'DUTY', message: translate('error.empty') });
    }
    if (!firstRow.DRIVER_COMPANY) {
        receivingErrors.push({ field: 'DRIVER_COMPANY', message: translate('error.empty') });
    }
    if (!firstRow.RECEIVING_TYPE) {
        receivingErrors.push({ field: 'RECEIVING_TYPE', message: translate('error.empty') });
    }
    // TRACKING_NUMBER is required only if DRIVER_COMPANY is not "Ostatní"
    if (firstRow.DRIVER_COMPANY && firstRow.DRIVER_COMPANY !== 'other' && !firstRow.TRACKING_NUMBER) {
        receivingErrors.push({ field: 'TRACKING_NUMBER', message: translate('error.empty') });
    }

    const eshopReceivingIdRaw = firstRow.ESHOP_RECEIVING_ID;
    const ESHOP_RECEIVING_ID =
        eshopReceivingIdRaw !== undefined && eshopReceivingIdRaw !== null && String(eshopReceivingIdRaw).trim() !== ''
            ? String(eshopReceivingIdRaw).trim()
            : undefined;

    return {
        ESHOP_RECEIVING_ID,
        SUPPLIER: fileData[0].SUPPLIER,
        TRACKING_NUMBER: fileData[0].TRACKING_NUMBER,
        ESTIMATED_DELIVERY: formatDate(fileData[0].ESTIMATED_DELIVERY),
        UNITS_AMOUNT: fileData[0].UNITS_AMOUNT,
        DESCRIPTION: fileData[0].DESCRIPTION,
        // Required fields
        DELIVERY_TYPE: fileData[0].DELIVERY_TYPE,
        DELIVERY_NOTE_NUMBER: fileData[0].DELIVERY_NOTE_NUMBER,
        CONTAINER_NUMBER: fileData[0].CONTAINER_NUMBER,
        ANNOUNCED_PALLETS_COUNT: fileData[0].ANNOUNCED_PALLETS_COUNT ? Number(fileData[0].ANNOUNCED_PALLETS_COUNT) : undefined,
        DUTY: fileData[0].DUTY ? Number(fileData[0].DUTY) : undefined,
        DRIVER_COMPANY: fileData[0].DRIVER_COMPANY,
        RECEIVING_TYPE: fileData[0].RECEIVING_TYPE,
        // Additional optional fields
        PRIORITY: fileData[0].PRIORITY ? Number(fileData[0].PRIORITY) : undefined,
        ESHOP_NOTE: fileData[0].ESHOP_NOTE,
        ASN_LABEL_EMAIL: fileData[0].ASN_LABEL_EMAIL,
        INCOTERMS: fileData[0].INCOTERMS,
        DRIVER_COMPANY_OTHER_NAME: fileData[0].DRIVER_COMPANY_OTHER_NAME,
        DRIVER_NAME: fileData[0].DRIVER_NAME,
        DRIVER_PHONE: fileData[0].DRIVER_PHONE,
        DRIVER_DISPATCHER_CONTACT: fileData[0].DRIVER_DISPATCHER_CONTACT,
        DRIVER_TRAILER_LICENSE_PLATE: fileData[0].DRIVER_TRAILER_LICENSE_PLATE,
        PRODUCTS: fileData.map((item: any, key: any): any => {
            const productObjItem = productMap[item.PRODUCT_SKU];

            const error = [];

            if (!item.PRODUCT_SKU) {
                error.push({ dataKey: 'PRODUCT_SKU', label: translate('error.empty') });
            }
            if (item.PRODUCT_SKU && !productObjItem) {
                error.push({ dataKey: 'PRODUCT_SKU', label: translate('error.wrongValue') });
            }
            if (!item.PRODUCT_QUANTITY) {
                error.push({ dataKey: 'PRODUCT_QUANTITY', label: translate('error.empty') });
            }

            const isInvalidSku = !!item.PRODUCT_SKU && !productObjItem;
            return {
                NAME: (productObjItem && productObjItem.name) || '',
                ID: productObjItem && productObjItem.id,
                SKU: String(item.PRODUCT_SKU),
                QUANTITY: item.PRODUCT_QUANTITY,
                LOT: item.PRODUCT_LOT && String(item.PRODUCT_LOT),
                EXPIRATION_DATE: formatDate(item.PRODUCT_EXPIRATION_DATE),
                EAN: productObjItem ? (productObjItem.ean ?? '') : '',
                DESCRIPTION: productObjItem ? (productObjItem.description ?? '') : '',
                PRODUCT_TYPE: isInvalidSku ? 'nonExistentSku' : (productObjItem ? (productObjItem.productType ?? '') : ''),
                error: error.length ? error : undefined,
                rowID: -1 - key,
                LINE_STATE: 'E',
                RECEIVINGS_PRODUCTS_ID: -(key + 1)
            };
        })
    };
};
